import { Badge, Box, Flex, Stack, Text, chakra } from '@chakra-ui/react';
import type { Decision, DecisionDay } from '@fluenceur/types';
import { NavArrowRight } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  TYPE_PALETTE,
  decisionHref,
  formatCalendarDay,
  gestureLabel,
  typeLabel,
  whoLabel,
} from './decision-format';

export interface DecisionDayDetailProps {
  day: DecisionDay;
  showType: boolean;
  /** Opens a decision in place (the UGC page's drawer); without it each decision is a link to its page. */
  onOpenDecision?: (decision: Decision) => void;
}

const ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  w: 'full',
  px: '10px',
  py: '8px',
  rounded: '8px',
  textAlign: 'left',
  color: 'ink',
  textDecoration: 'none',
  cursor: 'pointer',
  _hover: { bg: 'softCream' },
  _focusVisible: { outline: '2px solid', outlineColor: 'border.emphasized' },
} as const;

/**
 * The decisions of the selected day, under the strip: what to do and for
 * whom, each leading to where it is decided. The same list a phone shows on a
 * tap, where no tooltip can.
 */
export const DecisionDayDetail = ({ day, showType, onOpenDecision }: DecisionDayDetailProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const content = (decision: Decision): ReactNode => (
    <>
      <Box boxSize="6px" rounded="full" bg="yellow.600" flexShrink={0} aria-hidden="true" />
      <Text as="span" fontSize="13px" fontWeight={500} flex="1" minW="0" lineClamp={1}>
        {[gestureLabel(t, decision, locale), whoLabel(t, decision), decision.subject]
          .filter(Boolean)
          .join(' · ')}
      </Text>
      {showType ? (
        <Badge colorPalette={TYPE_PALETTE[decision.type]} variant="subtle" size="sm">
          {typeLabel(t, decision.type)}
        </Badge>
      ) : null}
      <NavArrowRight width={16} height={16} color="var(--chakra-colors-ink-muted)" aria-hidden />
    </>
  );

  return (
    <Stack
      as="section"
      gap="2px"
      mt="10px"
      pt="8px"
      borderTopWidth="1px"
      borderColor="border.subtle"
      aria-label={t(`decisionStrip.dayDetail`, { date: formatCalendarDay(day.date, locale) })}
      data-testid="decision-day-detail"
    >
      <chakra.ul listStyleType="none" m="0" p="0">
        {day.decisions.map((decision) => (
          <Flex as="li" key={decision.id}>
            {onOpenDecision ? (
              <chakra.button type="button" {...ROW_STYLE} onClick={() => onOpenDecision(decision)}>
                {content(decision)}
              </chakra.button>
            ) : (
              <chakra.a asChild {...ROW_STYLE}>
                <RouterLink to={decisionHref(decision)}>{content(decision)}</RouterLink>
              </chakra.a>
            )}
          </Flex>
        ))}
      </chakra.ul>
    </Stack>
  );
};
